import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Skeleton, SkeletonRows } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
	authorizeAssignmentPayment,
	type ContractorProfile,
	confirmCompletion,
	disputeCompletion,
	fetchContractors,
	type ProjectContractorItem,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	acceptProposal,
	browseDesigners,
	type DesignerProfile,
	hireContractor,
	inviteContractor,
	rejectProposal,
	removeContractor,
	sendRequestToDesigner,
} from "@/features/projects/services/projects-api.service";
import { useHomeownerProfile } from "@/features/settings/hooks/use-profile";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import {
	useProject,
	useProjectContractors,
	useProjectPayments,
	useProjectProposals,
	useProjectRequests,
} from "../../../hooks/use-projects";
import {
	getDisputeStatus,
	releasedAssignmentIdsFrom,
} from "../../../utils/assignment-payout";
import {
	Avatar,
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR as fmt,
	Icn,
	Pill,
	secondaryBtnStyle,
} from "../design-primitives";
import {
	EscrowCoverPanel,
	type EscrowShortfallRefusal,
} from "../escrow-cover-panel";
import {
	type ComparableProposalItem,
	ProposalCompareDialog,
} from "../proposals/proposal-compare-dialog";
import {
	type ProposalDecision,
	ProposalDecisionDialog,
} from "../proposals/proposal-decision-dialog";
import { ProposalDetail } from "../proposals/proposal-detail";

/**
 * The project statuses the backend accepts a top-up in,
 * `TOP_UP_PROJECT_STATUSES` in `payments.service.ts`: every status in which a
 * contractor can hold an assignment. The two lists have to change together; in
 * any other status the add-funds action could only be refused.
 */
const TOP_UP_PROJECT_STATUSES: ReadonlySet<string> = new Set([
	"in_progress",
	"completed",
	"cancelled",
]);

interface TeamMember {
	name: string;
	ini: string;
	tone: "ink" | "cognac" | "terra" | "amber";
	type: "designer" | "contractor" | "homeowner";
	role: string;
	resp: string;
	userId?: string;
}

// UI types
function useActionLoading() {
	const [loadingMap, setLoadingMap] = React.useState<Record<string, boolean>>(
		{},
	);

	const runWithLoading = React.useCallback(
		async (id: string, action: () => Promise<void>) => {
			setLoadingMap((prev) => ({ ...prev, [id]: true }));
			try {
				await action();
			} finally {
				setLoadingMap((prev) => ({ ...prev, [id]: false }));
			}
		},
		[],
	);

	return [loadingMap, runWithLoading] as const;
}

function getInitials(name: string): string {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

function getTone(index: number): TeamMember["tone"] {
	const tones: TeamMember["tone"][] = ["ink", "cognac", "terra", "amber"];
	return tones[index % tones.length];
}

function formatDate(dateStr?: string): string {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return date.toLocaleDateString("en-IN", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

interface SectionHeaderProps {
	eyebrow: string;
	titleText: string;
	accentText?: string;
	accentColor?: string;
	rightElement?: React.ReactNode;
}

function SectionHeader({
	eyebrow,
	titleText,
	accentText,
	accentColor = "var(--color-dashboard-accent)",
	rightElement,
}: SectionHeaderProps) {
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "flex-end",
				marginBottom: 16,
				gap: 16,
				flexWrap: "wrap",
			}}
		>
			<div>
				<Eyebrow>{eyebrow}</Eyebrow>
				<h3
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 26,
						letterSpacing: "-0.02em",
						color: "var(--color-dashboard-text)",
						margin: "6px 0 0",
					}}
				>
					{titleText}
					{accentText && (
						<>
							{" "}
							<span style={{ fontStyle: "italic", color: accentColor }}>
								{accentText}
							</span>
						</>
					)}
				</h3>
			</div>
			{rightElement}
		</div>
	);
}

interface ContractorCardProps {
	item: ProjectContractorItem;
	idx: number;
	actions?: React.ReactNode;
	onMessageUser?: (userId: string) => void;
}

interface ConfigLine {
	label: string;
	value: string;
}

/** What the contractor configured on their profile, for the lines that are set. */
function contractorConfigLines(
	c: ProjectContractorItem["contractor"],
): ConfigLine[] {
	if (!c) return [];
	const lines: ConfigLine[] = [];
	const location = [c.city, c.state].filter(Boolean).join(", ");
	if (location) lines.push({ label: "Location", value: location });
	if (c.serviceAreas?.length) {
		lines.push({ label: "Service areas", value: c.serviceAreas.join(", ") });
	}
	if (c.serviceRadiusKm != null) {
		lines.push({ label: "Service radius", value: `${c.serviceRadiusKm} km` });
	}
	if (c.experienceYears != null) {
		lines.push({ label: "Experience", value: `${c.experienceYears} yrs` });
	}
	const { priceRangeMin: min, priceRangeMax: max } = c;
	if (min != null && max != null) {
		lines.push({
			label: "Typical budget",
			value: min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`,
		});
	} else if (min != null || max != null) {
		lines.push({ label: "Typical budget", value: fmt((min ?? max) as number) });
	}
	return lines;
}

function ContractorCard({
	item,
	idx,
	actions,
	onMessageUser,
}: ContractorCardProps) {
	const c = item.contractor;
	const published = c?.publishedProfile;
	const name = published?.companyName || c?.name || "Contractor";
	const teamMembers = published?.teamMembers ?? [];
	const configLines = contractorConfigLines(c);
	const initials = getInitials(name);
	const tone = getTone(idx + 1);

	const statusLabels: Record<string, string> = {
		invited: "Invited",
		interested: "Interested",
		quote_submitted: "Quote Submitted",
		hired: "Active Partner",
		completion_requested: "Completion Pending",
		completed: "Completed",
		removed: "Removed",
		declined: "Declined",
	};

	const statusPills: Record<
		string,
		"awaiting" | "active" | "done" | "neutral"
	> = {
		invited: "awaiting",
		interested: "awaiting",
		quote_submitted: "awaiting",
		hired: "active",
		completion_requested: "awaiting",
		completed: "done",
		removed: "neutral",
		declined: "neutral",
	};

	return (
		<FloatPanel elev={1} padding={22}>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
				<Avatar initials={initials} tone={tone} size={50} />
				<div style={{ flex: 1, minWidth: 0 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 8,
							flexWrap: "wrap",
						}}
					>
						<div
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 19,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
							}}
						>
							{name}
						</div>
						<Pill tone={statusPills[item.status] || "neutral"} dot={false}>
							{statusLabels[item.status] || item.status}
						</Pill>
					</div>
					<div
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 9,
							letterSpacing: "0.12em",
							color: "var(--color-dashboard-accent-dark)",
							marginTop: 4,
							textTransform: "uppercase",
						}}
					>
						{c?.trades?.join(", ") || "Execution Partner"}
					</div>

					{configLines.map((line) => (
						<div
							key={line.label}
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted-dark)",
								marginTop: 6,
								lineHeight: 1.45,
							}}
						>
							<span style={{ fontWeight: 600 }}>{line.label}: </span>
							{line.value}
						</div>
					))}

					{teamMembers.length > 0 && (
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted-dark)",
								marginTop: 8,
								lineHeight: 1.45,
							}}
						>
							<span style={{ fontWeight: 600 }}>Team: </span>
							{teamMembers
								.map((member) => `${member.name} (${member.role})`)
								.join(", ")}
						</div>
					)}

					{item.quoteAmount != null && (
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-text)",
								marginTop: 8,
							}}
						>
							Quote:{" "}
							<span style={{ fontWeight: 600 }}>{fmt(item.quoteAmount)}</span>
						</div>
					)}

					{item.quoteDetails && (
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted-dark)",
								marginTop: 6,
								lineHeight: 1.45,
							}}
						>
							{item.quoteDetails}
						</div>
					)}

					{item.message && (
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12,
								color: "var(--color-dashboard-muted)",
								fontStyle: "italic",
								marginTop: 6,
							}}
						>
							Message: {item.message}
						</div>
					)}

					{item.hiredAt && (
						<div
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								color: "var(--color-dashboard-muted)",
								marginTop: 8,
								textTransform: "uppercase",
							}}
						>
							Hired {formatDate(item.hiredAt)}
						</div>
					)}

					{item.completedAt && (
						<div
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
								textTransform: "uppercase",
							}}
						>
							Completed {formatDate(item.completedAt)}
						</div>
					)}
				</div>
			</div>

			{(actions || (c?.userId && onMessageUser)) && (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginTop: 14,
						paddingTop: 14,
						borderTop: "1px solid var(--color-dashboard-border-soft)",
						gap: 12,
						flexWrap: "wrap",
					}}
				>
					<div>
						{c?.userId && onMessageUser && (
							<Btn
								variant="ghost"
								size="sm"
								icon={<Icn name="message" size={13} />}
								onClick={() => onMessageUser(c.userId)}
							>
								Message
							</Btn>
						)}
					</div>
					<div style={{ display: "flex", gap: 8 }}>{actions}</div>
				</div>
			)}
		</FloatPanel>
	);
}

function TeamSkeleton(): React.ReactElement {
	const { t } = useTranslation();
	return (
		<div
			role="status"
			style={{
				display: "grid",
				gridTemplateColumns: "repeat(2, 1fr)",
				gap: 16,
			}}
		>
			<span className="sr-only">{t("loaders.loadingTeam")}</span>
			{[1, 2, 3, 4].map((i) => (
				<FloatPanel key={i} elev={1} padding={22}>
					<div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
						<Skeleton className="size-12.5 rounded-full" />
						<div style={{ flex: 1 }}>
							<Skeleton className="h-5 w-3/5" />
							<Skeleton className="mt-2 h-3 w-2/5" />
							<Skeleton className="mt-3 h-3 w-4/5" />
						</div>
					</div>
				</FloatPanel>
			))}
		</div>
	);
}

interface HoTeamTabProps {
	projectId: string;
	onMessageUser: (userId: string) => void;
}

export function HoTeamTab({ projectId, onMessageUser }: HoTeamTabProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const accent = "var(--color-dashboard-accent)";

	const {
		data: project,
		isLoading: projectLoading,
		error: projectError,
		isFetching: projectFetching,
		refetch: refetchProject,
	} = useProject(projectId);
	const {
		data: contractorsData,
		isLoading: contractorsLoading,
		error: contractorsError,
		isFetching: contractorsFetching,
		refetch: refetchContractors,
	} = useProjectContractors(projectId);
	const contractors = contractorsData?.contractors;
	const disputeWindowHours = contractorsData?.disputeWindowHours ?? 72;
	const {
		data: homeownerProfile,
		isLoading: profileLoading,
		error: profileError,
		isFetching: profileFetching,
		refetch: refetchProfile,
	} = useHomeownerProfile();
	const { data: paymentsData, isLoading: paymentsLoading } =
		useProjectPayments(projectId);

	const {
		data: proposalsItems,
		isLoading: proposalsLoading,
		error: proposalsError,
		isFetching: proposalsFetching,
		refetch: refetchProposals,
	} = useProjectProposals(projectId);
	const {
		data: requestsItems,
		isLoading: requestsLoading,
		isFetching: requestsFetching,
		refetch: refetchRequests,
	} = useProjectRequests(projectId);

	const [isBrowsingDesigner, setIsBrowsingDesigner] = React.useState(false);
	const [designerBrowseResults, setDesignerBrowseResults] = React.useState<
		DesignerProfile[]
	>([]);
	const [designerBrowseLoading, setDesignerBrowseLoading] =
		React.useState(false);

	React.useEffect(() => {
		if (isBrowsingDesigner) {
			setDesignerBrowseLoading(true);
			browseDesigners({ limit: 6 })
				.then((res) => {
					setDesignerBrowseResults(res.designers);
				})
				.catch((err) => {
					console.error("Failed to browse designers:", err);
					toast.error("Failed to load designers");
				})
				.finally(() => {
					setDesignerBrowseLoading(false);
				});
		} else {
			setDesignerBrowseResults([]);
		}
	}, [isBrowsingDesigner]);

	const [invitingDesigner, withInviteDesignerLoading] = useActionLoading();
	const [acceptingProposal, withAcceptProposalLoading] = useActionLoading();
	const [rejectingProposal, withRejectProposalLoading] = useActionLoading();
	const [hiringContractor, withHireContractorLoading] = useActionLoading();
	const [removingContractor, withRemoveContractorLoading] = useActionLoading();
	const [confirmingCompletion, withConfirmCompletionLoading] =
		useActionLoading();
	const [authorizingPayment, withAuthorizePaymentLoading] = useActionLoading();
	const [disputingCompletion, withDisputeCompletionLoading] =
		useActionLoading();

	const handleInviteDesigner = (designerId: string) =>
		withInviteDesignerLoading(designerId, async () => {
			try {
				const res = await sendRequestToDesigner({ projectId, designerId });
				if (res.success) {
					toast.success("Request sent successfully");
					await Promise.all([refetchRequests(), refetchProposals()]);
					setIsBrowsingDesigner(false);
				} else {
					toast.error(res.error || "Failed to send request");
				}
			} catch (_err) {
				toast.error("Failed to send request");
			}
		});

	const handleAcceptProposal = (proposalId: string) =>
		withAcceptProposalLoading(proposalId, async () => {
			try {
				const res = await acceptProposal(proposalId);
				if (res.success) {
					void queryClient.invalidateQueries({
						queryKey: queryKeys.projects.lists(),
					});
					toast.success("Designer hired successfully");
					await Promise.all([refetchProject(), refetchProposals()]);
				} else {
					toast.error(res.error || "Failed to accept proposal");
				}
			} catch {
				toast.error("Failed to accept proposal");
			}
		});

	const handleRejectProposal = (proposalId: string) =>
		withRejectProposalLoading(proposalId, async () => {
			try {
				const res = await rejectProposal(proposalId);
				if (res.success) {
					toast.success("Proposal declined");
					await refetchProposals();
				} else {
					toast.error(res.error || "Failed to decline proposal");
				}
			} catch {
				toast.error("Failed to decline proposal");
			}
		});

	const [decision, setDecision] = React.useState<
		(ProposalDecision & { proposalId: string }) | null
	>(null);
	const [expandedProposalId, setExpandedProposalId] = React.useState<
		string | null
	>(null);
	const [compareIds, setCompareIds] = React.useState<string[]>([]);
	const [compareOpen, setCompareOpen] = React.useState(false);

	const requestDecision = (
		mode: ProposalDecision["mode"],
		item: ComparableProposalItem,
	) =>
		setDecision({
			mode,
			proposalId: item.proposal.id,
			designerName: item.designer.name,
			costEstimate: item.proposal.costEstimate,
		});

	const confirmDecision = () => {
		if (!decision) return;
		if (decision.mode === "accept") {
			void handleAcceptProposal(decision.proposalId);
		} else {
			void handleRejectProposal(decision.proposalId);
		}
	};

	const stopCardClick = (event: React.SyntheticEvent) =>
		event.stopPropagation();

	const toggleCompare = (proposalId: string) =>
		setCompareIds((current) =>
			current.includes(proposalId)
				? current.filter((id) => id !== proposalId)
				: current.length < 2
					? [...current, proposalId]
					: current,
		);

	const [showInvite, setShowInvite] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState("");
	const [suggestions, setSuggestions] = React.useState<ContractorProfile[]>([]);
	const [showSuggestions, setShowSuggestions] = React.useState(false);
	const [loadingSuggestions, setLoadingSuggestions] = React.useState(false);
	const [searchResults, setSearchResults] = React.useState<ContractorProfile[]>(
		[],
	);
	const [searching, setSearching] = React.useState(false);
	const [hasSearched, setHasSearched] = React.useState(false);
	const [searchFailed, setSearchFailed] = React.useState(false);
	const [loadingDefault, setLoadingDefault] = React.useState(false);
	const [inviting, setInviting] = React.useState<string | null>(null);
	const [closedExpanded, setClosedExpanded] = React.useState(false);

	const extendedContractors = React.useMemo(() => {
		return contractors || [];
	}, [contractors]);

	// A removed or declined contractor can be invited again, so only the
	// contractors still on the project are hidden from the invite search.
	const existingIds = React.useMemo(() => {
		return new Set(
			extendedContractors
				.filter((c) => c.status !== "removed" && c.status !== "declined")
				.map((c) => c.contractorId),
		);
	}, [extendedContractors]);

	// Debounced suggestions fetch
	React.useEffect(() => {
		if (!searchQuery.trim()) {
			setSuggestions((prev) => (prev.length > 0 ? [] : prev));
			setShowSuggestions((prev) => (prev ? false : prev));
			return;
		}
		setLoadingSuggestions(true);
		const timer = setTimeout(async () => {
			try {
				const result = await fetchContractors({
					query: searchQuery.trim(),
					limit: 8,
				});
				const filtered = result.contractors.filter(
					(c) => !existingIds.has(c.id),
				);
				setSuggestions(filtered);
				setShowSuggestions(filtered.length > 0);
			} catch (err) {
				console.error("Failed to load autocomplete suggestions:", err);
			} finally {
				setLoadingSuggestions(false);
			}
		}, 200);
		return () => clearTimeout(timer);
	}, [searchQuery, existingIds]);

	// Map of assignment IDs where payments are already released
	const releasedAssignmentIds = React.useMemo(
		() => releasedAssignmentIdsFrom(paymentsData?.transactions),
		[paymentsData],
	);

	// `releasedAssignmentIds` is derived from the payments query alone, so
	// between a successful authorization and the refresh that reflects it the
	// card goes on offering "Authorize Payment" for an escrow that is already
	// released. Nothing can be paid twice — authorizeAssignmentPayment takes the
	// assignment row FOR UPDATE and refuses a released escrow with a conflict —
	// but the control invites an action that can now only fail, and the failure
	// arrives as an error toast on a payment that in fact succeeded. A server
	// acceptance is something this component observed and that never stops being
	// true, so it is remembered until the refresh catches up.
	const [authorizedAssignmentIds, setAuthorizedAssignmentIds] = React.useState<
		ReadonlySet<string>
	>(() => new Set());

	const handleSearch = async () => {
		if (!searchQuery.trim()) return;
		setShowSuggestions(false);
		setSearching(true);
		setHasSearched(true);
		setSearchFailed(false);
		try {
			const result = await fetchContractors({ query: searchQuery.trim() });
			setSearchResults(
				result.contractors.filter((c) => !existingIds.has(c.id)),
			);
		} catch {
			setSearchFailed(true);
		} finally {
			setSearching(false);
		}
	};

	// Opening the panel lists verified contractors straight away, so a project
	// can be staffed by browsing rather than by already knowing a name.
	const loadDefaultContractors = React.useCallback(async () => {
		setLoadingDefault(true);
		setSearchFailed(false);
		try {
			const result = await fetchContractors({ limit: 12 });
			setSearchResults(
				result.contractors.filter((c) => !existingIds.has(c.id)),
			);
		} catch {
			setSearchFailed(true);
		} finally {
			setLoadingDefault(false);
		}
	}, [existingIds]);

	React.useEffect(() => {
		if (showInvite && !hasSearched) void loadDefaultContractors();
	}, [showInvite, hasSearched, loadDefaultContractors]);

	const selectSuggestion = (c: ContractorProfile) => {
		setSearchQuery(c.name);
		setShowSuggestions(false);
		setSuggestions([]);
	};

	const handleInvite = async (contractorId: string) => {
		setInviting(contractorId);
		try {
			const result = await inviteContractor(projectId, contractorId);
			if (result.success) {
				setSearchResults((prev) => prev.filter((c) => c.id !== contractorId));
				toast.success("Contractor invited");
				queryClient.invalidateQueries({
					queryKey: queryKeys.projects.contractors(projectId),
				});
				queryClient.invalidateQueries({
					queryKey: queryKeys.projects.detail(projectId),
				});
			} else {
				toast.error(result.error ?? "Failed to invite contractor");
			}
		} catch {
			toast.error("Failed to invite contractor");
		} finally {
			setInviting(null);
		}
	};

	// The last hire or contractor payout refused because the escrow did not
	// cover it. The escrow panel shows its message and opens the add-funds form
	// pre-filled with its shortfall; trying again is the same Hire or Authorize
	// Payment button.
	const [shortfallRefusal, setShortfallRefusal] =
		React.useState<EscrowShortfallRefusal | null>(null);
	const availableForHire = paymentsData?.summary?.availableForHire;
	const showEscrowPanel =
		typeof availableForHire === "number" &&
		project?.status !== undefined &&
		TOP_UP_PROJECT_STATUSES.has(project.status);

	const handleHire = (invitationId: string) => {
		if (hiringContractor[invitationId]) return;
		withHireContractorLoading(invitationId, async () => {
			try {
				const result = await hireContractor(invitationId);
				if (result.success) {
					setShortfallRefusal(null);
					toast.success("Contractor hired successfully");
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.contractors(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.detail(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.payments(projectId),
					});
				} else if (result.shortfall !== undefined && showEscrowPanel) {
					setShortfallRefusal({
						message: result.error ?? "Failed to hire contractor",
						shortfall: result.shortfall,
					});
				} else {
					// Every other refusal, and a shortfall with no panel on screen to
					// show it in.
					toast.error(result.error ?? "Failed to hire contractor");
				}
			} catch {
				toast.error("Failed to hire contractor");
			}
		});
	};

	// The refetch that brings the new available figure on screen.
	const handleFundsAdded = async (): Promise<void> => {
		setShortfallRefusal(null);
		await queryClient.invalidateQueries({
			queryKey: queryKeys.projects.payments(projectId),
		});
	};

	const handleRemove = (invitationId: string) => {
		if (removingContractor[invitationId]) return;
		withRemoveContractorLoading(invitationId, async () => {
			try {
				const result = await removeContractor(invitationId);
				if (result.success) {
					toast.success("Contractor removed");
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.contractors(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.detail(projectId),
					});
				} else {
					toast.error(result.error ?? "Failed to remove contractor");
				}
			} catch {
				toast.error("Failed to remove contractor");
			}
		});
	};

	const handleConfirmCompletion = (assignmentId: string) => {
		if (confirmingCompletion[assignmentId]) return;
		withConfirmCompletionLoading(assignmentId, async () => {
			try {
				const result = await confirmCompletion(assignmentId);
				if (result.success) {
					toast.success("Completion confirmed — contractor has been notified");
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.contractors(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.detail(projectId),
					});
				} else {
					toast.error(result.error ?? "Failed to confirm completion");
				}
			} catch {
				toast.error("Failed to confirm completion");
			}
		});
	};

	const handleAuthorizePayment = (assignmentId: string, _name: string) => {
		if (authorizingPayment[assignmentId]) return;
		withAuthorizePaymentLoading(assignmentId, async () => {
			try {
				const result = await authorizeAssignmentPayment(assignmentId);
				if (result.success) {
					setShortfallRefusal(null);
					setAuthorizedAssignmentIds((prev) => new Set(prev).add(assignmentId));
					toast.success(
						"Payment authorized — contractor will receive their payout",
					);
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.contractors(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.detail(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.payments(projectId),
					});
				} else if (result.shortfall !== undefined && showEscrowPanel) {
					setShortfallRefusal({
						message: result.error ?? "Failed to authorize payment",
						shortfall: result.shortfall,
					});
				} else {
					// As in `handleHire`: every other refusal, and a shortfall with no
					// panel on screen to show it in.
					toast.error(result.error ?? "Failed to authorize payment");
				}
			} catch {
				toast.error("Failed to authorize payment");
			}
		});
	};

	const handleDisputeCompletion = (assignmentId: string, _name: string) => {
		if (disputingCompletion[assignmentId]) return;
		withDisputeCompletionLoading(assignmentId, async () => {
			try {
				const result = await disputeCompletion(assignmentId);
				if (result.success) {
					toast.success(
						"Assignment disputed and reopened — contractor has been notified",
					);
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.contractors(projectId),
					});
					queryClient.invalidateQueries({
						queryKey: queryKeys.projects.detail(projectId),
					});
				} else {
					toast.error(result.error ?? "Failed to dispute completion");
				}
			} catch {
				toast.error("Failed to dispute completion");
			}
		});
	};

	const handleRetry = () => {
		refetchProject();
		refetchContractors();
		refetchProfile();
	};

	// The house rule for a retry affordance, documented on the shared
	// `QueryErrorState` in components/ui/query-error-state.tsx: feed it the
	// query's own `isFetching` so rapid clicks cannot stack refetches. Both Retry
	// buttons below run `handleRetry`, which fires all three, so both read all
	// three — a retry is in flight while any one of them is still running.
	const retryInFlight = !!(
		projectFetching ||
		contractorsFetching ||
		profileFetching
	);

	const isLoading =
		projectLoading ||
		contractorsLoading ||
		profileLoading ||
		paymentsLoading ||
		proposalsLoading ||
		requestsLoading;
	// A query that failed but still holds its last successful `data` is a failed
	// refresh, not an empty tab: query-core's "error" reducer branch spreads the
	// previous state, so `data` survives alongside `error`. Every mutating
	// handler above refreshes once its action has succeeded, so a full-tab error
	// gated on `error` alone would replace the tab — and the hire the homeowner
	// had just made — whenever one of those refreshes failed.
	//
	// Only `project` leaves the tab with nothing to say: without it there is no
	// designer and no project status, and the lead-designer block below would
	// assert "No designer hired yet" as fact. The homeowner's own card is
	// rendered under `{homeownerProfile && ...}`, so a failed profile load costs
	// that one card and never the tab.
	const hasNothingToRender = !!projectError && !project;
	// The contractors list drives five sections, every one of them gated on
	// `length > 0`, so with no cached list they all disappear without saying why
	// and the "No team members yet" empty state below asserts an absence the tab
	// cannot know. That is a reason to replace the partner half of the tab, not
	// the whole of it: the designer the homeowner has already hired, their own
	// card and the browse-and-request flow are all unaffected by this query.
	// Both controls that open the invite panel sit inside sections this state
	// removes — one in the "Awaiting their response" header, one in that empty
	// state — so the invite search, which de-duplicates against `existingIds`
	// derived from the same missing list, is unreachable while it holds.
	const contractorsUnavailable = !!contractorsError && !contractorsData;
	// The lead-designer counterpart: with no cached proposals list, the empty
	// state below would assert "No designer hired yet" as fact. That list holds
	// every request with its proposal, so a requests failure alone leaves it
	// true; the requests list only adds to the browse panel's already-requested
	// filter, which the proposals list covers on its own.
	const designerRequestsUnavailable = !!proposalsError && !proposalsItems;
	const designerRetryInFlight = !!(proposalsFetching || requestsFetching);
	const handleDesignerRetry = () => {
		refetchProposals();
		refetchRequests();
	};
	// Read only past the `hasNothingToRender` return below, so every failure it
	// covers is one the tab renders through. A contractors failure with nothing
	// cached is excluded because `contractorsUnavailable` states the stronger
	// thing in place of the sections it removes, and the banner would only
	// repeat it more weakly.
	const refreshFailed = !!(
		projectError ||
		profileError ||
		(contractorsError && contractorsData)
	);

	const hasHiredDesigner = !!project?.designer;
	const pendingDesignerItems = hasHiredDesigner
		? []
		: (proposalsItems || []).filter(
				(p) =>
					p.request.status === "pending" ||
					p.request.status === "proposal_submitted",
			);
	const noDesignerActivity =
		pendingDesignerItems.length === 0 && !isBrowsingDesigner;
	const showDesignerEmptyState =
		noDesignerActivity && !designerRequestsUnavailable;

	const comparableItems = pendingDesignerItems.filter(
		(item): item is ComparableProposalItem =>
			item.request.status === "proposal_submitted" && item.proposal !== null,
	);
	const selectedForCompare = comparableItems.filter((item) =>
		compareIds.includes(item.proposal.id),
	);
	const compareItems: [ComparableProposalItem, ComparableProposalItem] | null =
		selectedForCompare.length === 2
			? [selectedForCompare[0], selectedForCompare[1]]
			: null;

	if (isLoading) {
		return <TeamSkeleton />;
	}

	if (hasNothingToRender) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: 48,
					gap: 16,
				}}
			>
				<div
					style={{
						color: "var(--color-dashboard-pill-risk)",
						fontFamily: "var(--font-body)",
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					Failed to load team details
				</div>
				<StudioButton
					variant="secondary"
					onClick={handleRetry}
					disabled={retryInFlight}
					style={secondaryBtnStyle("md")}
				>
					{retryInFlight ? "Retrying..." : "Retry"}
				</StudioButton>
			</div>
		);
	}

	// Categorize project contractor assignments
	const activeExecutionPartners: ProjectContractorItem[] = [];
	const awaitingTheirResponse: ProjectContractorItem[] = [];
	const awaitingYourConfirmation: ProjectContractorItem[] = [];
	const completedExecutionPartners: ProjectContractorItem[] = [];
	const closedExecutionPartners: ProjectContractorItem[] = [];

	extendedContractors.forEach((c) => {
		switch (c.status) {
			case "hired":
				activeExecutionPartners.push(c);
				break;
			case "invited":
			case "interested":
			case "quote_submitted":
				awaitingTheirResponse.push(c);
				break;
			case "completion_requested":
				awaitingYourConfirmation.push(c);
				break;
			case "completed":
				completedExecutionPartners.push(c);
				break;
			case "removed":
			case "declined":
				closedExecutionPartners.push(c);
				break;
			default:
				break;
		}
	});

	const quotesCount = awaitingTheirResponse.filter(
		(c) => c.status === "quote_submitted" || c.status === "interested",
	).length;

	const designer = project?.designer;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
			<ProposalDecisionDialog
				decision={decision}
				onConfirm={confirmDecision}
				onCancel={() => setDecision(null)}
			/>
			<ProposalCompareDialog
				items={compareOpen ? compareItems : null}
				busy={
					Object.values(acceptingProposal).some(Boolean) ||
					Object.values(rejectingProposal).some(Boolean)
				}
				onOpenChange={setCompareOpen}
				onHire={(item) => requestDecision("accept", item)}
				onDecline={(item) => requestDecision("reject", item)}
			/>
			{/*
			 * A banner rather than a toast: the staleness lasts as long as the
			 * failed query does, while a toast would leave nothing on screen once
			 * it faded. This renders exactly while `refreshFailed` holds, so a
			 * later successful refetch clears it without any further code, and
			 * Retry re-runs all three of the queries it speaks for.
			 */}
			{refreshFailed && (
				<div
					role="alert"
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 12,
						padding: "12px 16px",
						borderRadius: 8,
						border: "1px solid var(--color-dashboard-danger-border)",
						background: "var(--color-dashboard-danger-bg)",
					}}
				>
					<div
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13,
							color: "var(--color-dashboard-accent-dark)",
						}}
					>
						Some team details failed to load. Parts of this tab may be missing
						or out of date.
					</div>
					<StudioButton
						variant="secondary"
						size="sm"
						onClick={handleRetry}
						disabled={retryInFlight}
						style={secondaryBtnStyle("sm")}
					>
						{retryInFlight ? "Retrying..." : "Retry"}
					</StudioButton>
				</div>
			)}

			{/* Core Team (Designer & Homeowner) */}
			<div>
				<SectionHeader
					eyebrow="PROJECT TEAM"
					titleText="Core"
					accentText="Team"
				/>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: 16,
					}}
				>
					{designer && (
						<FloatPanel elev={1} padding={22}>
							<div
								style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
							>
								<Avatar
									initials={getInitials(designer.name)}
									tone="ink"
									size={50}
								/>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 8,
										}}
									>
										<div
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												fontSize: 19,
												fontWeight: 500,
												color: "var(--color-dashboard-text)",
											}}
										>
											{designer.name}
										</div>
										<Pill tone="active" dot={false}>
											Design Team
										</Pill>
									</div>
									<div
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 9,
											color: "var(--color-dashboard-accent-dark)",
											marginTop: 4,
											textTransform: "uppercase",
										}}
									>
										Lead Designer
									</div>
									<div
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted-dark)",
											marginTop: 8,
											lineHeight: 1.5,
										}}
									>
										Design direction, material selections, client liaison and
										quality sign-off.
									</div>
								</div>
							</div>
							{designer.userId && (
								<div
									style={{
										marginTop: 14,
										paddingTop: 14,
										borderTop: "1px solid var(--color-dashboard-border-soft)",
									}}
								>
									<Btn
										variant="ghost"
										size="sm"
										icon={<Icn name="message" size={13} />}
										onClick={() => onMessageUser(designer.userId)}
									>
										Message
									</Btn>
								</div>
							)}
						</FloatPanel>
					)}

					{homeownerProfile && (
						<FloatPanel elev={1} padding={22}>
							<div
								style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
							>
								<Avatar
									initials={getInitials(homeownerProfile.name || "H")}
									tone="terra"
									size={50}
								/>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 8,
										}}
									>
										<div
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												fontSize: 19,
												fontWeight: 500,
												color: "var(--color-dashboard-text)",
											}}
										>
											{homeownerProfile.name || "Homeowner"}
										</div>
										<Pill tone="done" dot={false}>
											You
										</Pill>
									</div>
									<div
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 9,
											color: "var(--color-dashboard-accent-dark)",
											marginTop: 4,
											textTransform: "uppercase",
										}}
									>
										Homeowner
									</div>
									<div
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted-dark)",
											marginTop: 8,
											lineHeight: 1.5,
										}}
									>
										Project owner and manager.
									</div>
								</div>
							</div>
						</FloatPanel>
					)}
				</div>
			</div>

			{/* PRE-HIRE DESIGNER SECTION */}
			{!hasHiredDesigner && (
				<div style={{ marginBottom: 40 }}>
					<Eyebrow
						color="var(--color-dashboard-text)"
						style={{ fontSize: 11, letterSpacing: "0.15em", marginBottom: 16 }}
					>
						LEAD DESIGNER
					</Eyebrow>

					{noDesignerActivity && designerRequestsUnavailable && (
						<div role="alert">
							<FloatPanel
								elev={1}
								padding={32}
								style={{
									textAlign: "center",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: 16,
								}}
							>
								<div
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Couldn't load your designer requests and proposals.
								</div>
								<StudioButton
									variant="secondary"
									size="sm"
									onClick={handleDesignerRetry}
									disabled={designerRetryInFlight}
									style={secondaryBtnStyle("sm")}
								>
									{designerRetryInFlight ? "Retrying..." : "Retry"}
								</StudioButton>
							</FloatPanel>
						</div>
					)}

					{/* EMPTY STATE - NO REQUESTS */}
					{showDesignerEmptyState && (
						<div
							style={{
								background: "var(--color-dashboard-paper)",
								border: "1px dashed var(--color-border-default)",
								borderRadius: 13,
								padding: 32,
								textAlign: "center",
							}}
						>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									marginBottom: 16,
								}}
							>
								No designer hired yet. Find a professional to lead your project.
							</p>
							<Btn
								variant="primary"
								onClick={() => setIsBrowsingDesigner(true)}
								icon={
									<Icn
										name="search"
										size={16}
										color="var(--color-dashboard-on-fill)"
									/>
								}
							>
								Browse Designers
							</Btn>
						</div>
					)}

					{/* DESIGNER BROWSE PANEL */}
					{isBrowsingDesigner && (
						<div
							style={{
								background: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 13,
								padding: 24,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									marginBottom: 20,
								}}
							>
								<div
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 20,
										color: "var(--color-dashboard-text)",
									}}
								>
									Discover Designers
								</div>
								<Btn
									variant="ghost"
									size="sm"
									onClick={() => setIsBrowsingDesigner(false)}
									icon={<Icn name="x" size={16} />}
								>
									Cancel
								</Btn>
							</div>

							{designerBrowseLoading ? (
								<SkeletonRows
									label="Loading designers..."
									className="py-5"
									rowClassName="h-16 rounded-xl"
								/>
							) : (
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 16,
									}}
								>
									{designerBrowseResults.map((d) => {
										const alreadyRequested =
											proposalsItems?.some(
												(p) =>
													p.designer.id === d.id &&
													p.request.status !== "rejected",
											) ||
											requestsItems?.some(
												(r) => r.designerId === d.id && r.status !== "rejected",
											);
										if (alreadyRequested) return null;

										return (
											<FloatPanel
												key={d.id}
												elev={1}
												padding={16}
												onClick={() => navigate(`/designers/${d.id}`)}
												style={{ cursor: "pointer" }}
											>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 12,
													}}
												>
													<Avatar
														initials={getInitials(d.name)}
														size={40}
														tone="amber"
													/>
													<div style={{ flex: 1, minWidth: 0 }}>
														<div
															style={{
																fontFamily: "var(--font-display)",
																fontWeight: 500,
																fontSize: 16,
																color: "var(--color-dashboard-text)",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}}
														>
															{d.name}
														</div>
														<div
															style={{
																fontFamily: "var(--font-body)",
																fontSize: 12,
																color: "var(--color-dashboard-muted)",
															}}
														>
															{d.styles?.join(", ") || "Design Professional"}
														</div>
													</div>
													<StudioButton
														variant="secondary"
														size="sm"
														onClick={(
															e: React.MouseEvent<HTMLButtonElement>,
														) => {
															e.stopPropagation();
															handleInviteDesigner(d.id);
														}}
														disabled={invitingDesigner[d.id]}
														style={secondaryBtnStyle("sm")}
													>
														{invitingDesigner[d.id] ? "Sending..." : "Request"}
													</StudioButton>
												</div>
											</FloatPanel>
										);
									})}
									{designerBrowseResults.length === 0 && (
										<div
											style={{
												gridColumn: "1 / -1",
												textAlign: "center",
												color: "var(--color-dashboard-muted)",
												fontSize: 13,
												padding: "20px 0",
											}}
										>
											No designers available in your area yet.
										</div>
									)}
								</div>
							)}
						</div>
					)}

					{/* PENDING REQUESTS & PROPOSALS */}
					{!isBrowsingDesigner && pendingDesignerItems.length > 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
							{pendingDesignerItems.map((item) => {
								const proposal = item.proposal;
								const isProposal =
									item.request.status === "proposal_submitted" && proposal;

								return (
									<FloatPanel
										key={item.request.id}
										elev={1}
										padding={20}
										onClick={
											expandedProposalId !== null &&
											expandedProposalId === proposal?.id
												? undefined
												: () => navigate(`/designers/${item.designer.id}`)
										}
										style={{ cursor: "pointer" }}
									>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												gap: 16,
											}}
										>
											<div style={{ display: "flex", gap: 14 }}>
												<Avatar
													initials={getInitials(item.designer.name)}
													size={48}
													tone="ink"
												/>
												<div>
													<div
														style={{
															display: "flex",
															gap: 8,
															alignItems: "center",
														}}
													>
														<span
															style={{
																fontFamily: "var(--font-display)",
																fontSize: 18,
																fontWeight: 500,
																color: "var(--color-dashboard-text)",
															}}
														>
															{item.designer.name}
														</span>
														<Pill
															tone={isProposal ? "awaiting" : "neutral"}
															dot={false}
														>
															{isProposal
																? "Proposal Received"
																: "Request Sent"}
														</Pill>
													</div>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 13,
															color: "var(--color-dashboard-muted-dark)",
															marginTop: 4,
														}}
													>
														{item.designer.firmName || "Independent Designer"}
													</div>
												</div>
											</div>

											{isProposal && proposal && (
												<div style={{ display: "flex", gap: 8 }}>
													<Btn
														variant="ghost"
														size="sm"
														onClick={(
															e: React.MouseEvent<HTMLButtonElement>,
														) => {
															e.stopPropagation();
															requestDecision("reject", { ...item, proposal });
														}}
														disabled={
															rejectingProposal[proposal.id] ||
															acceptingProposal[proposal.id]
														}
													>
														Decline
													</Btn>
													<Btn
														variant="primary"
														size="sm"
														onClick={(
															e: React.MouseEvent<HTMLButtonElement>,
														) => {
															e.stopPropagation();
															requestDecision("accept", { ...item, proposal });
														}}
														disabled={
															acceptingProposal[proposal.id] ||
															rejectingProposal[proposal.id]
														}
													>
														Hire Designer
													</Btn>
												</div>
											)}
										</div>

										{isProposal && proposal && (
											<div
												style={{
													marginTop: 20,
													paddingTop: 16,
													borderTop:
														"1px solid var(--color-dashboard-border-soft)",
													display: "flex",
													gap: 40,
												}}
											>
												<div>
													<Eyebrow style={{ fontSize: 9 }}>
														PROPOSED COST
													</Eyebrow>
													<div
														style={{
															fontFamily: "var(--font-display)",
															fontSize: 24,
															color: "var(--color-dashboard-text)",
															marginTop: 4,
														}}
													>
														{fmt(proposal.costEstimate)}
													</div>
												</div>
												<div>
													<Eyebrow style={{ fontSize: 9 }}>TIMELINE</Eyebrow>
													<div
														style={{
															fontFamily: "var(--font-display)",
															fontSize: 24,
															color: "var(--color-dashboard-text)",
															marginTop: 4,
														}}
													>
														{proposal.timelineWeeks} weeks
													</div>
												</div>
											</div>
										)}

										{isProposal && proposal && (
											<div style={{ marginTop: 16 }}>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 16,
													}}
												>
													<Btn
														variant="ghost"
														size="sm"
														onClick={(
															e: React.MouseEvent<HTMLButtonElement>,
														) => {
															e.stopPropagation();
															setExpandedProposalId(
																expandedProposalId === proposal.id
																	? null
																	: proposal.id,
															);
														}}
													>
														{expandedProposalId === proposal.id
															? t("proposalCard.hideDetails")
															: t("proposalCard.viewDetails")}
													</Btn>
													{comparableItems.length >= 2 && (
														<label
															onClick={stopCardClick}
															onKeyDown={stopCardClick}
															style={{
																display: "flex",
																alignItems: "center",
																gap: 6,
																fontFamily: "var(--font-body)",
																fontSize: 13,
																color: "var(--color-dashboard-muted-dark)",
															}}
														>
															<input
																type="checkbox"
																checked={compareIds.includes(proposal.id)}
																disabled={
																	!compareIds.includes(proposal.id) &&
																	selectedForCompare.length >= 2
																}
																aria-label={t("proposalCompare.selectAria", {
																	name: item.designer.name,
																})}
																onChange={() => toggleCompare(proposal.id)}
															/>
															{t("proposalCompare.select")}
														</label>
													)}
												</div>
												{expandedProposalId === proposal.id && (
													<div style={{ marginTop: 16 }}>
														<ProposalDetail proposal={proposal} />
													</div>
												)}
											</div>
										)}
									</FloatPanel>
								);
							})}

							{comparableItems.length >= 2 && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
										fontFamily: "var(--font-body)",
										fontSize: 13,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									<StudioButton
										variant="secondary"
										size="sm"
										disabled={selectedForCompare.length !== 2}
										onClick={() => setCompareOpen(true)}
										style={secondaryBtnStyle("sm")}
									>
										{t("proposalCompare.open", {
											count: selectedForCompare.length,
										})}
									</StudioButton>
									{selectedForCompare.length !== 2 && (
										<span>{t("proposalCompare.hint")}</span>
									)}
								</div>
							)}

							<div style={{ marginTop: 8 }}>
								<Btn
									variant="ghost"
									size="sm"
									onClick={() => setIsBrowsingDesigner(true)}
									icon={<Icn name="plus" size={16} />}
								>
									Invite another designer
								</Btn>
							</div>
						</div>
					)}
				</div>
			)}

			{project?.status === "in_progress" && !contractorsUnavailable && (
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					{showInvite ? (
						<StudioButton
							size="sm"
							variant="secondary"
							style={secondaryBtnStyle("sm")}
							onClick={() => setShowInvite(false)}
						>
							<Icn name="plus" size={13} />
							{t("teamInvite.cancel")}
						</StudioButton>
					) : (
						<Btn
							variant="primary"
							size="sm"
							icon={<Icn name="plus" size={13} />}
							onClick={() => setShowInvite(true)}
						>
							{t("teamInvite.invite")}
						</Btn>
					)}
				</div>
			)}

			{/* Invite Search Panel */}
			{showInvite && (
				<FloatPanel
					elev={2}
					padding={22}
					style={{ border: "1px solid var(--color-dashboard-border)" }}
				>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 16,
						}}
					>
						<Eyebrow>FIND AND INVITE CONTRACTORS</Eyebrow>
						<Btn
							variant="ghost"
							size="xs"
							onClick={() => {
								setShowInvite(false);
								setSearchQuery("");
								setSearchResults([]);
								setSuggestions([]);
								setHasSearched(false);
							}}
						>
							Cancel
						</Btn>
					</div>

					<div style={{ position: "relative", marginBottom: 16 }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								background: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 13,
								padding: "4px 4px 4px 18px",
								gap: 12,
							}}
						>
							<Icn
								name="search"
								size={16}
								color="var(--color-dashboard-muted)"
							/>
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search by name or trade..."
								style={{
									flex: 1,
									height: 40,
									border: 0,
									background: "transparent",
									outline: "none",
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-text)",
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSearch();
									if (e.key === "Escape") setShowSuggestions(false);
								}}
							/>
							{loadingSuggestions && (
								<Spinner
									label={t("loaders.searchingContractors")}
									className="mr-2 size-4"
								/>
							)}
							<Btn
								variant="primary"
								size="sm"
								onClick={handleSearch}
								style={{ height: 38 }}
								disabled={searching}
							>
								{searching ? "Searching..." : "Search"}
							</Btn>
						</div>

						{/* Suggestions autocomplete dropdown */}
						{showSuggestions && suggestions.length > 0 && (
							<div
								style={{
									position: "absolute",
									top: "calc(100% + 6px)",
									left: 0,
									right: 0,
									background: "var(--color-dashboard-paper)",
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 13,
									boxShadow:
										"0 8px 24px -6px color-mix(in srgb, var(--color-dashboard-shadow-ink) 12%, transparent)",
									zIndex: 50,
									overflow: "hidden",
								}}
							>
								{suggestions.map((c, i) => (
									<button
										key={c.id}
										type="button"
										onClick={() => selectSuggestion(c)}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 12,
											width: "100%",
											padding: "12px 18px",
											background: "transparent",
											border: "none",
											borderBottom:
												i < suggestions.length - 1
													? "1px solid var(--color-dashboard-border-soft)"
													: "none",
											cursor: "pointer",
											textAlign: "left",
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.background =
												"var(--color-dashboard-surface)";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = "transparent";
										}}
									>
										<Avatar
											initials={getInitials(c.name)}
											tone={getTone(i)}
											size={30}
										/>
										<div>
											<div
												style={{
													fontFamily: "var(--font-display)",
													fontStyle: "italic",
													fontSize: 15,
													fontWeight: 500,
													color: "var(--color-dashboard-text)",
												}}
											>
												{c.name}
											</div>
											<div
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													color: "var(--color-dashboard-muted)",
													textTransform: "uppercase",
													marginTop: 2,
												}}
											>
												{c.trades?.join(", ")}
											</div>
										</div>
									</button>
								))}
							</div>
						)}
					</div>

					{/* Search Results */}
					{showInvite && (
						<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
							{searchFailed ? (
								<div
									role="alert"
									style={{
										textAlign: "center",
										padding: "16px 0",
										color: "var(--color-dashboard-muted)",
										fontSize: 13,
									}}
								>
									Couldn't load contractors.{" "}
									<Btn
										variant="ghost"
										size="sm"
										onClick={() =>
											hasSearched ? handleSearch() : loadDefaultContractors()
										}
									>
										Retry
									</Btn>
								</div>
							) : (searching || loadingDefault) &&
								searchResults.length === 0 ? (
								<SkeletonRows label="Loading contractors" rows={3} />
							) : searchResults.length === 0 ? (
								<div
									style={{
										textAlign: "center",
										padding: "16px 0",
										color: "var(--color-dashboard-muted)",
										fontStyle: "italic",
										fontSize: 13,
									}}
								>
									{hasSearched
										? `No verified contractors found for "${searchQuery}"`
										: "No verified contractors available to invite right now"}
								</div>
							) : (
								searchResults.map((c, idx) => (
									<div
										key={c.id}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "12px 16px",
											background: "var(--color-dashboard-surface)",
											borderRadius: 10,
											border: "1px solid var(--color-dashboard-border)",
											flexWrap: "wrap",
											gap: 12,
										}}
									>
										<div
											style={{ display: "flex", alignItems: "center", gap: 12 }}
										>
											<Avatar
												initials={getInitials(c.name)}
												tone={getTone(idx)}
												size={36}
											/>
											<div>
												<div
													style={{
														fontFamily: "var(--font-display)",
														fontStyle: "italic",
														fontSize: 16,
														fontWeight: 500,
														color: "var(--color-dashboard-text)",
													}}
												>
													{c.name}
												</div>
												<div
													style={{
														fontFamily: "var(--font-mono)",
														fontSize: 9,
														color: "var(--color-dashboard-muted)",
														textTransform: "uppercase",
														marginTop: 2,
													}}
												>
													{c.trades?.join(", ")} | {c.experienceYears}y exp
												</div>
											</div>
										</div>
										<Btn
											variant="primary"
											size="sm"
											onClick={() => handleInvite(c.id)}
											disabled={inviting === c.id}
										>
											{inviting === c.id ? "Inviting..." : "Invite"}
										</Btn>
									</div>
								))
							)}
						</div>
					)}
				</FloatPanel>
			)}

			{/* What a hire can be paid from, and adding to it */}
			{showEscrowPanel && (
				<EscrowCoverPanel
					projectId={projectId}
					availableForHire={availableForHire}
					refusal={shortfallRefusal}
					onDismissRefusal={() => setShortfallRefusal(null)}
					onFunded={handleFundsAdded}
				/>
			)}

			{/* Awaiting Your Confirmation (Completion Requested) */}
			{awaitingYourConfirmation.length > 0 && (
				<div>
					<SectionHeader
						eyebrow="PENDING APPROVALS"
						titleText="Awaiting your"
						accentText="confirmation"
					/>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 16,
						}}
					>
						{awaitingYourConfirmation.map((item, idx) => (
							<ContractorCard
								key={item.id}
								item={item}
								idx={idx}
								onMessageUser={onMessageUser}
								actions={
									<Btn
										variant="primary"
										size="sm"
										onClick={() => handleConfirmCompletion(item.id)}
										disabled={confirmingCompletion[item.id]}
									>
										{confirmingCompletion[item.id]
											? "Confirming..."
											: "Confirm Completion"}
									</Btn>
								}
							/>
						))}
					</div>
				</div>
			)}

			{/* Active Execution Partners */}
			{activeExecutionPartners.length > 0 && (
				<div>
					<SectionHeader
						eyebrow="SITE WORKERS"
						titleText="Active Execution"
						accentText="partners"
					/>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 16,
						}}
					>
						{activeExecutionPartners.map((item, idx) => (
							<ContractorCard
								key={item.id}
								item={item}
								idx={idx}
								onMessageUser={onMessageUser}
								actions={
									<Btn
										variant="ghost"
										size="sm"
										style={{ color: "var(--color-dashboard-pill-risk)" }}
										icon={
											<Icn
												name="trash"
												size={13}
												color="var(--color-dashboard-pill-risk)"
											/>
										}
										onClick={() => handleRemove(item.id)}
										disabled={removingContractor[item.id]}
									>
										{removingContractor[item.id] ? "Removing..." : "Remove"}
									</Btn>
								}
							/>
						))}
					</div>
				</div>
			)}

			{/* Awaiting Their Response */}
			{awaitingTheirResponse.length > 0 && (
				<div>
					<SectionHeader
						eyebrow="GETTING QUOTES"
						titleText="Awaiting their"
						accentText="response"
					/>

					{/* Compare Quotes CTA Card */}
					{quotesCount >= 2 && (
						<FloatPanel
							elev={2}
							padding={20}
							style={{
								border: `1px solid ${accent}`,
								background:
									"color-mix(in srgb, var(--color-dashboard-accent) 2%, transparent)",
								marginBottom: 16,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 16,
								}}
							>
								<div>
									<div
										style={{
											fontFamily: "var(--font-display)",
											fontStyle: "italic",
											fontSize: 18,
											fontWeight: 500,
											color: "var(--color-dashboard-text)",
										}}
									>
										You have {quotesCount} quotes ready for comparison
									</div>
									<div
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted-dark)",
											marginTop: 4,
										}}
									>
										Compare their rates, scope, and timeline side-by-side.
									</div>
								</div>
								<Btn
									variant="primary"
									size="sm"
									onClick={() => {
										toast.info(
											"Quote comparison will be available in Phase 3!",
										);
									}}
								>
									Compare Quotes
								</Btn>
							</div>
						</FloatPanel>
					)}

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 16,
						}}
					>
						{awaitingTheirResponse.map((item, idx) => (
							<ContractorCard
								key={item.id}
								item={item}
								idx={idx}
								onMessageUser={onMessageUser}
								actions={
									<>
										{(item.status === "quote_submitted" ||
											item.status === "interested") && (
											<Btn
												variant="primary"
												size="sm"
												onClick={() => handleHire(item.id)}
												disabled={
													hiringContractor[item.id] ||
													removingContractor[item.id]
												}
											>
												{hiringContractor[item.id] ? "Hiring..." : "Hire"}
											</Btn>
										)}
										<Btn
											variant="ghost"
											size="sm"
											style={{ color: "var(--color-dashboard-pill-risk)" }}
											icon={
												<Icn
													name="trash"
													size={13}
													color="var(--color-dashboard-pill-risk)"
												/>
											}
											onClick={() => handleRemove(item.id)}
											disabled={
												removingContractor[item.id] || hiringContractor[item.id]
											}
										>
											{removingContractor[item.id] ? "Removing..." : "Remove"}
										</Btn>
									</>
								}
							/>
						))}
					</div>
				</div>
			)}

			{/* Completed Execution Partners (Completed) */}
			{completedExecutionPartners.length > 0 && (
				<div>
					<SectionHeader
						eyebrow="ARCHIVED PARTNERS"
						titleText="Completed"
						accentText="assignments"
					/>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 16,
						}}
					>
						{completedExecutionPartners.map((item, idx) => {
							const isReleased =
								releasedAssignmentIds.has(item.id) ||
								authorizedAssignmentIds.has(item.id);
							// The designer hired this contractor and pays them directly,
							// so there is no escrow payout to authorize:
							// `authorizeAssignmentPayment` refuses one.
							const designerPaid = item.paymentArrangement === "designer_paid";
							const disputeStatus = getDisputeStatus(
								item.completedAt,
								disputeWindowHours,
							);
							return (
								<ContractorCard
									key={item.id}
									item={item}
									idx={idx}
									onMessageUser={onMessageUser}
									actions={
										isReleased ? (
											<Pill tone="done" dot={false}>
												Payment Released
											</Pill>
										) : (
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: 6,
													width: "100%",
												}}
											>
												<div style={{ display: "flex", gap: 8 }}>
													<StudioButton
														variant="secondary"
														size="sm"
														onClick={() =>
															handleDisputeCompletion(
																item.id,
																item.contractor?.name || "Contractor",
															)
														}
														disabled={
															disputingCompletion[item.id] ||
															authorizingPayment[item.id] ||
															disputeStatus.isExpired
														}
														style={secondaryBtnStyle("sm")}
													>
														{disputingCompletion[item.id]
															? "Disputing..."
															: disputeStatus.label}
													</StudioButton>
													{!designerPaid && (
														<Btn
															variant="primary"
															size="sm"
															onClick={() =>
																handleAuthorizePayment(
																	item.id,
																	item.contractor?.name || "Contractor",
																)
															}
															disabled={
																authorizingPayment[item.id] ||
																disputingCompletion[item.id]
															}
														>
															{authorizingPayment[item.id]
																? "Authorizing..."
																: "Authorize Payment"}
														</Btn>
													)}
												</div>
												{designerPaid && (
													<span
														style={{
															fontSize: 11,
															color: "var(--color-dashboard-muted)",
															textAlign: "left",
															marginTop: 2,
														}}
													>
														Paid by your designer
													</span>
												)}
												{disputeStatus.isExpired && (
													<span
														style={{
															fontSize: 11,
															color: "var(--color-dashboard-muted)",
															textAlign: "left",
															marginTop: 2,
														}}
													>
														Dispute window expired
													</span>
												)}
											</div>
										)
									}
								/>
							);
						})}
					</div>
				</div>
			)}

			{/*
			 * Stands in for the five sections the contractors list feeds — four
			 * above this point and the closed/declined one below — all of which
			 * render nothing when that list is missing, and for the empty state
			 * below, which would otherwise call the absence a fact. Its Retry
			 * re-runs only the query it speaks for, unlike the tab-wide banner's.
			 */}
			{contractorsUnavailable && (
				<div role="alert">
					<FloatPanel
						elev={1}
						padding={40}
						style={{
							textAlign: "center",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 16,
						}}
					>
						<Icn
							name="users"
							size={32}
							color="var(--color-dashboard-accent-dark)"
						/>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							Your execution partners could not be loaded. This list is unknown,
							not empty.
						</div>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => refetchContractors()}
							disabled={contractorsFetching}
							style={secondaryBtnStyle("sm")}
						>
							{contractorsFetching ? "Retrying..." : "Retry"}
						</StudioButton>
					</FloatPanel>
				</div>
			)}

			{/* Empty State when no designer and no contractors exist */}
			{!contractorsUnavailable &&
				!designer &&
				awaitingTheirResponse.length === 0 &&
				activeExecutionPartners.length === 0 &&
				awaitingYourConfirmation.length === 0 &&
				completedExecutionPartners.length === 0 && (
					<FloatPanel
						elev={1}
						padding={40}
						style={{
							textAlign: "center",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 16,
						}}
					>
						<Icn name="users" size={32} color="var(--color-dashboard-muted)" />
						<div>
							<div
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 20,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
								}}
							>
								No team members yet
							</div>
							<div
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted)",
									marginTop: 6,
								}}
							>
								{project?.status === "in_progress"
									? t("teamEmpty.invite")
									: t("teamEmpty.inviteLater")}
							</div>
						</div>
					</FloatPanel>
				)}

			{/* Closed / Declined Assignments */}
			{closedExecutionPartners.length > 0 && (
				<div style={{ marginTop: 12 }}>
					<button
						type="button"
						onClick={() => setClosedExpanded(!closedExpanded)}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							border: 0,
							background: "transparent",
							cursor: "pointer",
							padding: 0,
							fontFamily: "var(--font-mono)",
							fontSize: 10,
							fontWeight: 600,
							color: "var(--color-dashboard-muted)",
							textTransform: "uppercase",
							letterSpacing: "0.18em",
						}}
					>
						<Icn
							name={closedExpanded ? "x" : "plus"}
							size={12}
							color="var(--color-dashboard-muted)"
						/>
						Closed / Declined Assignments ({closedExecutionPartners.length})
					</button>

					{closedExpanded && (
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 16,
								marginTop: 16,
							}}
						>
							{closedExecutionPartners.map((item, idx) => (
								<FloatPanel key={item.id} elev={1} padding={22}>
									<div
										style={{
											display: "flex",
											alignItems: "flex-start",
											gap: 14,
										}}
									>
										<Avatar
											initials={getInitials(item.contractor?.name || "C")}
											tone={getTone(idx)}
											size={50}
										/>
										<div style={{ flex: 1, minWidth: 0 }}>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													gap: 8,
												}}
											>
												<div
													style={{
														fontFamily: "var(--font-display)",
														fontStyle: "italic",
														fontSize: 19,
														fontWeight: 500,
														color: "var(--color-dashboard-text)",
													}}
												>
													{item.contractor?.name || "Contractor"}
												</div>
												<Pill tone="neutral" dot={false}>
													{item.status === "removed" ? "Removed" : "Declined"}
												</Pill>
											</div>
											<div
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													color: "var(--color-dashboard-accent-dark)",
													marginTop: 4,
													textTransform: "uppercase",
												}}
											>
												{item.contractor?.trades?.join(", ") ||
													"Execution Partner"}
											</div>
											{item.quoteAmount != null && (
												<div
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 13,
														color: "var(--color-dashboard-muted-dark)",
														marginTop: 8,
													}}
												>
													Quote: {fmt(item.quoteAmount)}
												</div>
											)}
										</div>
									</div>
								</FloatPanel>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
